import { useEffect, useState, useCallback } from "react";
import { api, apiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { DateInput } from "@/components/DateInput";
import { fmtNumber, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { UserPlus, KeyRound, Power, Trash2, Plus, AlertTriangle, Pencil } from "lucide-react";
import HelpButton from "@/components/HelpButton";

const TABS = [
  ["users", "Utilizatori"],
  ["financers", "Finanțatori"],
  ["insurers", "Asigurători"],
  ["vat", "Cote TVA"],
  ["fuel_accounts", "Carburanți · Conturi"],
  ["fuel_splits", "Carburanți · Split CC"],
  ["imports", "Jurnal importuri"],
  ["audit", "Jurnal audit"],
  ["reset", "Resetare date"],
];

export default function Admin() {
  const [tab, setTab] = useState("users");
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Administrare</h1>
        <HelpButton anchor="admin" />
      </div>
      <div className="flex flex-wrap gap-1 border-b">
        {TABS.map(([k, l]) => (
          <button
            key={k}
            data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === k ? "border-[#FFC800] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
      <div data-testid="admin-panel-container">
        {tab === "users" && <Users />}
        {tab === "financers" && <Financers />}
        {tab === "insurers" && <Insurers />}
        {tab === "vat" && <Vat />}
        {tab === "fuel_accounts" && <FuelAccounts />}
        {tab === "fuel_splits" && <FuelSplits />}
        {tab === "imports" && <ImportLog />}
        {tab === "audit" && <AuditLog />}
        {tab === "reset" && <ResetData />}
      </div>
    </div>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user" });
  const [editUser, setEditUser] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data.users);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("Utilizator creat");
      setForm({ email: "", name: "", password: "", role: "user" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggle = async (id) => { await api.post(`/admin/users/${id}/deactivate`); load(); };
  const reset = async (id) => {
    const p = window.prompt("Parolă nouă (min. 12 caractere):");
    if (!p) return;
    try { await api.post(`/admin/users/${id}/reset-password`, { password: p }); toast.success("Parolă resetată"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Ștergeți definitiv utilizatorul ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Utilizator șters"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/users/${editUser.id}`, {
        name: editUser.name, email: editUser.email, role: editUser.role,
      });
      toast.success("Utilizator actualizat");
      setEditUser(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-5 gap-3 items-end bg-slate-50 p-4 rounded border">
        <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
        <div className="space-y-1"><Label className="text-xs">Nume</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
        <div className="space-y-1"><Label className="text-xs">Parolă (min. 12)</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
        <div className="space-y-1"><Label className="text-xs">Rol</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="user">Utilizator</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
          </Select>
        </div>
        <Button onClick={create} className="bg-slate-900 text-white gap-2" data-testid="create-user-btn"><UserPlus className="h-4 w-4" /> Adaugă</Button>
      </div>
      <div className="border rounded overflow-auto">
        <table className="w-full text-sm jh-table">
          <thead className="bg-slate-100"><tr><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Nume</th><th className="text-left px-4 py-2">Rol</th><th className="text-left px-4 py-2">Status</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-2">{u.email}</td>
                <td className="px-4 py-2">{u.name}</td>
                <td className="px-4 py-2"><Badge variant={u.role === "admin" ? "default" : "secondary"}>{u.role}</Badge></td>
                <td className="px-4 py-2">{u.active ? <span className="text-emerald-600">Activ</span> : <span className="text-red-600">Inactiv</span>}</td>
                <td className="px-4 py-2 text-right flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => setEditUser({ ...u })} className="gap-1" data-testid={`edit-user-${u.email}`}><Pencil className="h-3.5 w-3.5" />Editează</Button>
                  <Button size="sm" variant="ghost" onClick={() => reset(u.id)} className="gap-1"><KeyRound className="h-3.5 w-3.5" />Parolă</Button>
                  <Button size="sm" variant="ghost" onClick={() => toggle(u.id)} className="gap-1"><Power className="h-3.5 w-3.5" />{u.active ? "Dezactivează" : "Activează"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(u)} className="gap-1 text-red-600" data-testid={`delete-user-${u.email}`}><Trash2 className="h-3.5 w-3.5" />Șterge</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Editează utilizator</DialogTitle>
            <DialogDescription className="sr-only">Modificați nume, email și rol.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Nume</Label><Input value={editUser.name || ""} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="edit-user-name" /></div>
              <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={editUser.email || ""} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} data-testid="edit-user-email" /></div>
              <div className="space-y-1"><Label className="text-xs">Rol</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="user">Utilizator</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUser(null)}>Anulează</Button>
            <Button onClick={saveEdit} className="bg-slate-900 text-white" data-testid="save-user-btn">Salvează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Financers() {
  const [fins, setFins] = useState([]);
  const load = useCallback(async () => { const { data } = await api.get("/admin/financers"); setFins(data.financers); }, []);
  useEffect(() => { load(); }, [load]);
  const update = async (code, vat_base) => { await api.put(`/admin/financers/${code}`, { vat_base }); toast.success("Actualizat"); load(); };
  return (
    <div className="border rounded overflow-auto max-w-2xl">
      <table className="w-full text-sm jh-table">
        <thead className="bg-slate-100"><tr><th className="text-left px-4 py-2">Cod</th><th className="text-left px-4 py-2">Nume</th><th className="text-left px-4 py-2">Bază TVA</th></tr></thead>
        <tbody>
          {fins.map((f) => (
            <tr key={f.code} className="hover:bg-slate-50">
              <td className="px-4 py-2 font-medium">{f.code}</td>
              <td className="px-4 py-2">{f.name}</td>
              <td className="px-4 py-2">
                <Select value={f.vat_base} onValueChange={(v) => update(f.code, v)}>
                  <SelectTrigger className="w-40" data-testid={`vat-base-${f.code}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="interest">Dobândă</SelectItem><SelectItem value="total">Total (P+D)</SelectItem></SelectContent>
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Insurers() {
  const [ins, setIns] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(async () => { const { data } = await api.get("/prepaid/insurers"); setIns(data.insurers); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post("/prepaid/admin/insurers", { name }); toast.success("Asigurător adăugat"); setName(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/prepaid/admin/insurers/${id}`); load(); };
  const tpl = async () => { try { await downloadFile("/prepaid/admin/insurers/template", "Sablon_Asiguratori.xlsx"); } catch (e) { toast.error("Descărcare eșuată"); } };
  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/prepaid/admin/insurers/import", fd);
      toast.success(`${data.added} asigurători adăugați`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Import eșuat"); }
    e.target.value = "";
  };
  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex flex-wrap items-end gap-3 bg-slate-50 p-4 rounded border">
        <div className="space-y-1 flex-1 min-w-[200px]"><Label className="text-xs">Nume asigurător</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-insurer-name" /></div>
        <Button onClick={add} className="bg-slate-900 text-white gap-2" data-testid="add-insurer-btn"><Plus className="h-4 w-4" /> Adaugă</Button>
        <Button onClick={tpl} variant="outline" className="gap-2">Șablon</Button>
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm cursor-pointer hover:bg-slate-100" data-testid="import-insurers-label">
          Import .xlsx
          <input type="file" accept=".xlsx" className="hidden" onChange={doImport} data-testid="import-insurers-input" />
        </label>
      </div>
      <div className="border rounded overflow-auto max-h-[60vh]">
        <table className="w-full text-sm jh-table">
          <thead className="bg-slate-100 sticky top-0"><tr><th className="text-left px-4 py-2">Nume</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {ins.map((i) => (
              <tr key={i.id} className="hover:bg-slate-50" data-testid={`insurer-row-${i.name}`}>
                <td className="px-4 py-2">{i.name}</td>
                <td className="px-4 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => del(i.id)} className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button></td>
              </tr>
            ))}
            {ins.length === 0 && <tr><td colSpan={2} className="px-4 py-8 text-center text-slate-400">Niciun asigurător. Adăugați sau importați.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Vat() {
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState({ rate: 19, valid_from: "2017-01-01", financer_code: "" });
  const load = useCallback(async () => { const { data } = await api.get("/admin/vat-rates"); setRates(data.vat_rates); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post("/admin/vat-rates", { rate: parseFloat(form.rate), valid_from: form.valid_from, financer_code: form.financer_code || null }); toast.success("Cotă adăugată"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/vat-rates/${id}`); load(); };
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="grid sm:grid-cols-4 gap-3 items-end bg-slate-50 p-4 rounded border">
        <div className="space-y-1"><Label className="text-xs">Cotă %</Label><Input type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="vat-rate-input" /></div>
        <div className="space-y-1"><Label className="text-xs">Valabil de la</Label><DateInput value={form.valid_from} onChange={(v) => setForm({ ...form, valid_from: v })} data-testid="vat-validfrom-input" /></div>
        <div className="space-y-1"><Label className="text-xs">Finanțator (opțional)</Label>
          <Select value={form.financer_code || "all"} onValueChange={(v) => setForm({ ...form, financer_code: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="vat-financer-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Toți</SelectItem><SelectItem value="DL">DL</SelectItem><SelectItem value="BP">BP</SelectItem><SelectItem value="RL">RL</SelectItem><SelectItem value="PL">PL</SelectItem></SelectContent>
          </Select>
        </div>
        <Button onClick={add} className="bg-slate-900 text-white gap-2" data-testid="add-vat-btn"><Plus className="h-4 w-4" /> Adaugă</Button>
      </div>
      <div className="border rounded overflow-auto">
        <table className="w-full text-sm jh-table">
          <thead className="bg-slate-100"><tr><th className="text-right px-4 py-2">Cotă</th><th className="text-left px-4 py-2">Valabil de la</th><th className="text-left px-4 py-2">Finanțator</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {rates.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-4 py-2 text-right tabular">{fmtNumber(r.rate, 0)}%</td>
                <td className="px-4 py-2 tabular">{fmtDate(r.valid_from)}</td>
                <td className="px-4 py-2">{r.financer_code || "Toți"}</td>
                <td className="px-4 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => del(r.id)} className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ImportLog() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/import-log").then((r) => setLogs(r.data.logs)); }, []);
  return (
    <div className="border rounded overflow-auto">
      <table className="w-full text-sm jh-table">
        <thead className="bg-slate-100"><tr><th className="text-left px-4 py-2">Data</th><th className="text-left px-4 py-2">Utilizator</th><th className="text-left px-4 py-2">Secțiune</th><th className="text-left px-4 py-2">Fișier</th><th className="text-right px-4 py-2">Rânduri</th><th className="text-left px-4 py-2">Mod</th></tr></thead>
        <tbody>
          {logs.map((l, i) => (
            <tr key={i} className="hover:bg-slate-50">
              <td className="px-4 py-2 tabular">{new Date(l.timestamp).toLocaleString("ro-RO")}</td>
              <td className="px-4 py-2">{l.user_email}</td>
              <td className="px-4 py-2">{l.section}</td>
              <td className="px-4 py-2">{l.file_name}</td>
              <td className="px-4 py-2 text-right tabular">{l.rows_read}</td>
              <td className="px-4 py-2">{l.mode}</td>
            </tr>
          ))}
          {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Niciun import înregistrat.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [f, setF] = useState({ user_email: "", entity: "" });
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/audit-log", { params: { user_email: f.user_email || undefined, entity: f.entity || undefined } });
    setLogs(data.logs);
  }, [f]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <Input placeholder="Filtru utilizator" value={f.user_email} onChange={(e) => setF({ ...f, user_email: e.target.value })} className="w-56" data-testid="audit-user-filter" />
        <Input placeholder="Filtru entitate" value={f.entity} onChange={(e) => setF({ ...f, entity: e.target.value })} className="w-56" data-testid="audit-entity-filter" />
      </div>
      <div className="border rounded overflow-auto max-h-[60vh]">
        <table className="w-full text-sm jh-table">
          <thead className="bg-slate-100 sticky top-0"><tr><th className="text-left px-4 py-2">Data</th><th className="text-left px-4 py-2">Utilizator</th><th className="text-left px-4 py-2">Acțiune</th><th className="text-left px-4 py-2">Entitate</th><th className="text-left px-4 py-2">Fișier</th></tr></thead>
          <tbody>
            {logs.map((l, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-2 tabular whitespace-nowrap">{new Date(l.timestamp).toLocaleString("ro-RO")}</td>
                <td className="px-4 py-2">{l.user_email}</td>
                <td className="px-4 py-2">{l.action}</td>
                <td className="px-4 py-2">{l.entity}</td>
                <td className="px-4 py-2">{l.import_file || ""}</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Niciun eveniment.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FuelAccounts() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ prefix: "", account: "60221" });
  const load = useCallback(async () => { const { data } = await api.get("/fuel/product-accounts"); setRows(data.product_accounts); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post("/fuel/admin/product-accounts", form); toast.success("Regulă adăugată"); setForm({ prefix: "", account: "60221" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/fuel/admin/product-accounts/${id}`); load(); };
  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-slate-600">Contul se determină după primele 3 litere ale produsului (ca <span className="font-mono">LEFT(produs,3)</span> în Excel). Prefixe cu alt cont sunt excluse din raport și listate ca avertisment.</p>
      <div className="flex flex-wrap items-end gap-3 bg-slate-50 p-4 rounded border">
        <div className="space-y-1"><Label className="text-xs">Prefix (3 litere)</Label><Input value={form.prefix} maxLength={3} onChange={(e) => setForm({ ...form, prefix: e.target.value })} data-testid="fuel-acc-prefix" className="w-32" /></div>
        <div className="space-y-1"><Label className="text-xs">Cont</Label><Input value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} data-testid="fuel-acc-account" className="w-32" /></div>
        <Button onClick={add} className="bg-slate-900 text-white gap-2" data-testid="add-fuel-acc-btn"><Plus className="h-4 w-4" /> Adaugă</Button>
      </div>
      <div className="border rounded overflow-auto">
        <table className="w-full text-sm jh-table">
          <thead className="bg-slate-100"><tr><th className="text-left px-4 py-2">Prefix</th><th className="text-left px-4 py-2">Cont</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`fuel-acc-row-${r.prefix}`}>
                <td className="px-4 py-2 font-mono font-medium">{r.prefix}</td>
                <td className="px-4 py-2 font-mono">{r.account}</td>
                <td className="px-4 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => del(r.id)} className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">Nicio regulă.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FuelSplits() {
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("");
  const [parts, setParts] = useState([{ cc: "", share: 50 }, { cc: "", share: 50 }]);
  const load = useCallback(async () => { const { data } = await api.get("/fuel/cost-center-splits"); setRows(data.splits); }, []);
  useEffect(() => { load(); }, [load]);

  const setPart = (i, key, val) => setParts(parts.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
  const addPartRow = () => setParts([...parts, { cc: "", share: 0 }]);
  const removePartRow = (i) => setParts(parts.filter((_, idx) => idx !== i));
  const shareSum = parts.reduce((a, p) => a + (parseFloat(p.share) || 0), 0);

  const add = async () => {
    try {
      await api.post("/fuel/admin/cost-center-splits", {
        source, parts: parts.filter((p) => p.cc).map((p) => ({ cc: p.cc, share: (parseFloat(p.share) || 0) / 100 })),
      });
      toast.success("Regulă split adăugată");
      setSource(""); setParts([{ cc: "", share: 50 }, { cc: "", share: 50 }]);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/fuel/admin/cost-center-splits/${id}`); load(); };

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-slate-600">Un centru de cost „sursă" se repartizează pe mai multe CC finale, după cote care trebuie să însumeze 100%. Restul CC se înregistrează direct.</p>
      <div className="space-y-3 bg-slate-50 p-4 rounded border">
        <div className="space-y-1 max-w-xs"><Label className="text-xs">Sursă (ex. 06_36)</Label><Input value={source} onChange={(e) => setSource(e.target.value)} data-testid="fuel-split-source" /></div>
        <div className="space-y-2">
          {parts.map((p, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="space-y-1"><Label className="text-xs">CC final</Label><Input value={p.cc} onChange={(e) => setPart(i, "cc", e.target.value)} data-testid={`fuel-split-cc-${i}`} className="w-28 font-mono" /></div>
              <div className="space-y-1"><Label className="text-xs">Cotă %</Label><Input type="number" value={p.share} onChange={(e) => setPart(i, "share", e.target.value)} data-testid={`fuel-split-share-${i}`} className="w-24" /></div>
              {parts.length > 1 && <Button size="sm" variant="ghost" onClick={() => removePartRow(i)} className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>}
            </div>
          ))}
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={addPartRow} className="gap-1"><Plus className="h-3.5 w-3.5" /> CC</Button>
            <span className={`text-xs ${shareSum === 100 ? "text-emerald-600" : "text-red-600"}`}>Total cote: {shareSum}%</span>
          </div>
        </div>
        <Button onClick={add} disabled={!source || shareSum !== 100} className="bg-slate-900 text-white gap-2" data-testid="add-fuel-split-btn"><Plus className="h-4 w-4" /> Adaugă regulă</Button>
      </div>
      <div className="border rounded overflow-auto">
        <table className="w-full text-sm jh-table">
          <thead className="bg-slate-100"><tr><th className="text-left px-4 py-2">Sursă</th><th className="text-left px-4 py-2">Repartizare</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`fuel-split-row-${r.source}`}>
                <td className="px-4 py-2 font-mono font-medium">{r.source}</td>
                <td className="px-4 py-2">{r.parts.map((p) => `${p.cc} (${Math.round(p.share * 100)}%)`).join(", ")}</td>
                <td className="px-4 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => del(r.id)} className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">Nicio regulă de split.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResetData() {
  const SECTIONS = [["leasing", "Leasing"], ["prepaid", "Cheltuieli în avans"], ["fuel", "Carburanți"]];
  const [section, setSection] = useState("leasing");
  const [confirmation, setConfirmation] = useState("");
  const label = SECTIONS.find((s) => s[0] === section)[1];
  const doReset = async () => {
    try {
      const { data } = await api.post("/admin/reset-data", { section, confirmation });
      toast.success("Date șterse: " + JSON.stringify(data.deleted));
      setConfirmation("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-xl space-y-4 border border-red-200 bg-red-50 rounded p-5">
      <div className="flex items-center gap-2 text-red-700 font-semibold"><AlertTriangle className="h-5 w-5" /> Resetare date secțiune</div>
      <p className="text-sm text-slate-600">Șterge toate datele secțiunii selectate. Utilizatorii, configurarea și jurnalul de audit sunt păstrate. Acțiunea este ireversibilă și se înregistrează în audit.</p>
      <div className="space-y-1">
        <Label className="text-xs">Secțiune</Label>
        <Select value={section} onValueChange={(v) => { setSection(v); setConfirmation(""); }}>
          <SelectTrigger className="w-64" data-testid="reset-section-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SECTIONS.map((s) => <SelectItem key={s[0]} value={s[0]}>{s[1]}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Tastați exact „{label}" pentru confirmare</Label>
        <Input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} data-testid="reset-confirm-input" />
      </div>
      <Button onClick={doReset} disabled={confirmation !== label} variant="destructive" className="gap-2" data-testid="reset-data-btn">
        <Trash2 className="h-4 w-4" /> Șterge datele
      </Button>
    </div>
  );
}
